import * as Yup from "yup";
import { format } from "date-fns";

import { cents } from "../../../utils";
import { FREQUENCIES } from "../../../../Recurrence";
import { castInputToNumber, createForm } from "../../forms";

import type {
  LoanFundingMethod,
  LoanPaymentFormValues,
  LoanPaymentType,
} from "./types";
import type { TypeSafeReactLocalization } from "../../../composites";

export const loanPaymentValidationSchema = ({
  getString,
}: TypeSafeReactLocalization) => {
  const required = getString("form-generic-validation-required");
  return Yup.object<LoanPaymentFormValues>().shape({
    toAccountId: Yup.string().required(required),
    paymentType: Yup.string().required(required),
    fundingMethod: Yup.string()
      .required(required)
      .when("paymentType", {
        is: "principal",
        then: (schema) =>
          schema.oneOf(
            ["bank"],
            getString("loan-payment-form-error-funding-method"),
          ),
        otherwise: (schema) => schema.oneOf(["bank", "card"]),
      }),
    amount: Yup.number()
      .transform(castInputToNumber)
      .required(required)
      .min(1, getString("form-generic-validation-positive-number")),
    date: Yup.string().required(),
    frequency: Yup.string().oneOf(Object.keys(FREQUENCIES)).required(),
    fromAccountId: Yup.string().when("fundingMethod", {
      is: "bank",
      then: (schema) => schema.required(required),
      otherwise: (schema) => schema.optional(),
    }),
    paymentAccountName: Yup.string().when("fundingMethod", {
      is: "bank",
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
    fromAccountSource: Yup.string().when("fundingMethod", {
      is: "bank",
      then: (schema) => schema.oneOf(["institution", "external"]).required(),
      otherwise: (schema) => schema.optional(),
    }),
    cardUUID: Yup.string().when("fundingMethod", {
      is: "card",
      then: (schema) => schema.required(required),
      otherwise: (schema) => schema.optional(),
    }),
    memo: Yup.string().max(
      128,
      getString("form-generic-validation-limit-characters", { count: 128 }),
    ),
  });
};

export const loanPaymentFormInitialValues: LoanPaymentFormValues = {
  toAccountId: "" as API.AccountId,
  paymentType: "" as LoanPaymentType,
  fundingMethod: "bank" as LoanFundingMethod,
  amount: cents(0),
  fromAccountId: "" as API.AccountId,
  paymentAccountName: "",
  fromAccountSource: "institution",
  cardUUID: "",
  date: format(Date.now(), "yyyy-MM-dd"),
  frequency: "ONCE",
  memo: "",
};

export const loanPaymentForm = createForm<LoanPaymentFormValues>({
  initialValues: loanPaymentFormInitialValues,
  translateValidationSchema: loanPaymentValidationSchema,
});
